/**
 * MobileTrainingReturnPrompt — app-level orchestrator for the mobile return +
 * self-attestation flow. Mounted once (App.tsx). It:
 *
 *   1. Detects return to OptiBay (existing useTrainingSessionDetection hook +
 *      a mount check that covers iOS evicting/reloading Safari).
 *   2. Reads the client-only mobile launch record (if valid + unexpired).
 *   3. Computes an honest "time away" estimate (never verified watch time).
 *   4. Shows MobileTrainingReturnDialog.
 *   5. On explicit "I finished watching" (completed) or "Do it later" (partial),
 *      persists a LOW-CONFIDENCE, unverified outcome via useMobileTrainingOutcome.
 *
 * Every exit path clears the launch record. Nothing auto-completes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { clock } from '@/utils/clock';
import { useTrainingSessionDetection } from '@/hooks/useTrainingSessionDetection';
import {
  useMobileTrainingOutcome,
  type MobileTrainingOutcome,
} from '@/hooks/useMobileTrainingOutcome';
import { TrainingQuizModal } from '@/components/training/TrainingQuizModal';
import { useTrainingQuizLauncher } from '@/hooks/useTrainingQuizLauncher';
import {
  readValidMobileTrainingLaunch,
  clearMobileTrainingLaunch,
  type MobileTrainingLaunchRecord,
} from '@/lib/training/mobileTrainingLaunchState';
import {
  computeMobileTrainingEstimate,
  formatAwayDuration,
  formatKnownDuration,
  type MobileTrainingEstimate,
} from '@/lib/training/mobileTrainingEstimate';
import { MobileTrainingReturnDialog } from '@/components/training/MobileTrainingReturnDialog';

export function MobileTrainingReturnPrompt() {
  const [record, setRecord] = useState<MobileTrainingLaunchRecord | null>(null);
  const [estimate, setEstimate] = useState<MobileTrainingEstimate | null>(null);
  // De-dupe: don't re-open the dialog for the same launch (keyed by its start).
  const promptedForRef = useRef<number | null>(null);
  const outcome = useMobileTrainingOutcome();
  // Phase Q1 — optional post-completion quiz. OFF until the quiz backend is live.
  // The launcher gates on the flag AND an approved-quiz pre-check.
  const quiz = useTrainingQuizLauncher();

  const maybeShow = useCallback(() => {
    const nowMs = clock.nowMs();
    const rec = readValidMobileTrainingLaunch(nowMs);
    if (!rec) return;
    if (promptedForRef.current === rec.launchedAtMs) return;
    promptedForRef.current = rec.launchedAtMs;
    setRecord(rec);
    setEstimate(
      computeMobileTrainingEstimate({
        launchedAtMs: rec.launchedAtMs,
        returnMs: nowMs,
        durationMinutes: rec.durationMinutes,
      }),
    );
  }, []);

  // Primary trigger: existing return-to-app detection (visibility + focus).
  // minSecondsAway: 1 so even a short return prompts (no completion pressure).
  useTrainingSessionDetection({
    enabled: true,
    minSecondsAway: 1,
    onReturn: () => maybeShow(),
  });

  // Safety net for the eviction/reload case, where no in-session hidden→visible
  // transition fires: check once on mount too.
  useEffect(() => {
    maybeShow();
  }, [maybeShow]);

  const close = useCallback(() => {
    setRecord(null);
    setEstimate(null);
  }, []);

  const dismissAndClear = useCallback(() => {
    clearMobileTrainingLaunch();
    close();
  }, [close]);

  const quizModal = quiz.target ? (
    <TrainingQuizModal
      open
      artifactId={quiz.target.artifactId}
      sessionId={quiz.target.sessionId}
      adapter={quiz.adapter}
      prefetchedQuiz={quiz.target.quiz}
      onClose={quiz.close}
      onResult={quiz.handleResult}
    />
  ) : null;

  // The return dialog is gone once a record is cleared, but a quiz opened from
  // "I finished watching" must persist past that close.
  if (!record || !estimate) return quizModal;

  const rec = record;
  const est = estimate;
  const durationKnown = est.estimatedCompletionPct !== null;

  const persist = async (mode: MobileTrainingOutcome) => {
    // Persist ONLY on explicit user action; low-confidence, unverified.
    // 'completed' = "I finished watching"; 'partial' = "Do it later" (opened on
    // mobile, not complete — kept in Passport History so it can be resumed).
    if (rec.technicianProfileId) {
      const args = {
        outcome: mode,
        existingSessionId: rec.existingSessionId ?? null,
        artifactId: rec.artifactId,
        ticketId: rec.ticketId,
        technicianProfileId: rec.technicianProfileId,
        providerUrl: rec.providerUrl,
        elapsedSeconds: est.elapsedSeconds,
        durationSeconds: est.durationSeconds,
        estimatedCompletionPct: est.estimatedCompletionPct,
      } as const;
      // On completion, if the quiz feature is live, chain into the knowledge
      // check for THIS session. Otherwise keep the fire-and-forget write.
      if (mode === 'completed' && quiz.quizEnabled) {
        const res = await outcome.mutateAsync(args);
        clearMobileTrainingLaunch();
        close();
        // Open the knowledge check ONLY if an approved quiz exists (launcher gate).
        if (res?.sessionId) await quiz.launch(rec.artifactId, res.sessionId);
        return;
      }
      outcome.mutate(args);
    }
    clearMobileTrainingLaunch();
    close();
  };

  // Unified with the desktop completion options. Only "completed" persists a
  // completion (+ chains the quiz); "not yet" records nothing (stays unrecorded).
  const handleCompleted = () => void persist('completed');
  const handlePartial = () => void persist('partial');
  const handleSkip = () => void persist('skipped');
  const handleNotYet = () => dismissAndClear();

  const handleCompleteOnDesktop = () => {
    toast('Complete on desktop for verified credit', {
      description:
        'Open this training on a desktop with the OptiBay Companion installed to record verified completion.',
    });
    dismissAndClear();
  };

  return (
    <>
      <MobileTrainingReturnDialog
        open
        awayText={formatAwayDuration(est.elapsedSeconds)}
        durationKnown={durationKnown}
        durationText={
          rec.durationMinutes != null ? formatKnownDuration(rec.durationMinutes) : null
        }
        estimatedCompletionPct={est.estimatedCompletionPct}
        onCompleted={handleCompleted}
        onPartial={handlePartial}
        onNotYet={handleNotYet}
        onSkip={handleSkip}
        onCompleteOnDesktop={handleCompleteOnDesktop}
        onClose={dismissAndClear}
      />
      {quizModal}
    </>
  );
}
